import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API, { fmt } from '@/lib/api';
import { PdfViewer } from '@/components/PdfViewer';
import { DocumentSourcePanel } from '@/components/DocumentSourcePanel';
import { DocumentImageViewer } from '@/components/DocumentImageViewer';
import { IncomingDocumentContext, IncomingDocumentField as Veld, IncomingDocumentSection as Kaart } from '@/components/IncomingDocumentFields';
import { useDocumentSource } from '@/hooks/useDocumentSource';
import { InkomendeBetalingDialog } from '@/components/InkomendeBetalingDialog';
import { InkomendeBetalingen } from '@/components/InkomendeBetalingen';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, CheckCircle, XCircle, CurrencyEur, DownloadSimple,
  MagicWand, Warning, CheckSquare, Paperclip, FileText,
  CreditCard, Receipt, Buildings, CaretDown, CaretUp, CaretRight, Link as LinkSimple, SkipForward, Wallet
} from '@phosphor-icons/react';

// Hiërarchische categorie-structuur — identiek aan InkomendeDocumentenPage (15 groepen + 100+ subs)
const CATEGORIE_GROEPEN = [
  { groep: 'Gebouwen / lokalen / kantoren / terreinen', items: ['Elektriciteit (productie + kantoor)','Gas / verwarming','Waterverbruik','Brandverzekering','Onderhoud machines (gebouwgebonden installaties)','Herstellingen gebouw','Schoonmaak (extern bedrijf)','Beveiliging (alarm, camera\'s)','Onroerende voorheffing','Syndicuskosten','Afvalverwerking / containerkosten','Internet / bekabeling in gebouw'] },
  { groep: 'Algemene kosten', items: ['Telefoon & internet abonnementen','Softwarelicenties (ERP, Billit, CAD, etc.)','Kantoorbenodigdheden','Drukwerk (labels, stickers, catalogi)','Bankkosten','Boekhouder / accountant','Advieskosten (juridisch/fiscaal)','Marketing & reclame','Website / hosting','Opleidingen','Lidmaatschappen / federaties'] },
  { groep: 'Investeringen', items: ['Machines (rollforming lijnen, decoilers)','Gereedschappen (matrijzen, molds)','IT hardware (computers, servers)','Magazijninrichting (rekken, stellingen)','Heftrucks / intern transport','Bouw of renovatie van productiehal','Zonnepanelen / energie-installaties'] },
  { groep: 'Personeel / uitzendkrachten', items: ['Lonen arbeiders & bedienden','Interimkrachten','Vakantiegeld','Bonussen','Werkkledij / PBM (veiligheidsschoenen, helmen)','Opleidingen personeel','Maaltijdcheques','Transportvergoeding'] },
  { groep: 'Administratieve formaliteiten', items: ['Notariskosten','Publicaties (Belgisch Staatsblad)','Vergunningen (VLAREM, milieu)','Certificeringen (CE, ISO)','Douanedocumenten','Juridische dossiers','Registraties (BTW, EORI)'] },
  { groep: 'Rollend materieel (andere)', items: ['Heftrucks','Magazijnvoertuigen','Palletwagens (elektrisch)','Onderhoud & herstellingen','Batterijen / laadstations'] },
  { groep: 'Onderaannemingen', items: ['Transporteurs (nationaal/internationaal)','Loonwerk (externe productie)','Montageploegen','Verpakkingsdiensten','Technisch onderhoud extern','IT outsourcing'] },
  { groep: '(Handels)goederen', items: ['Grondstoffen (staal DX51D, coils, slitband)','Halffabricaten','Inkoop profielen','Bevestigingsmaterialen (pluggen, schroeven)','Verpakkingsmateriaal (karton, folie)','Labels / private labeling'] },
  { groep: 'Huur', items: ['Huur bedrijfspand','Huur magazijn','Huur machines','Huur voertuigen','Leasing (indien als huur geboekt)'] },
  { groep: 'Rollend materieel (personenwagen)', items: ['Aankoop / leasing wagen','Brandstof wagen','Verzekering wagen','Onderhoud wagen','Banden','Taksen'] },
  { groep: 'Rollend materieel (motorfiets)', items: ['Aankoop motorfiets','Brandstof motorfiets','Onderhoud motorfiets','Verzekering motorfiets'] },
  { groep: 'Rollend materieel (lichte vracht)', items: ['Bestelwagens / camionettes','Brandstof bestelwagen','Onderhoud bestelwagen','Leasing bestelwagen','Verzekering bestelwagen','Tolkosten'] },
  { groep: 'Erelonen / commissies', items: ['Makelaars','Verkoperscommissie','Agenten','Consultancy fees','Technische adviseurs'] },
  { groep: 'Verzekeringen', items: ['BA verzekering','Arbeidsongevallen','Machinebreuk','Transportverzekering','Kredietverzekering','Brandverzekering (algemeen)'] },
  { groep: 'Sociale bijdragen', items: ['RSZ bijdragen','Zelfstandigen bijdragen','Sectorbijdragen','Pensioenfonds'] },
];

const STATUS_STAPPEN = [
  { key: 'te_verwerken', label: 'Ontvangen', color: '#0891B2' },
  { key: 'verwerkt', label: 'Gecontroleerd', color: '#7C3AED' },
  { key: 'goedgekeurd', label: 'Goedgekeurd', color: '#059669' },
  { key: 'geboekt', label: 'Geboekt', color: '#002FA7' },
  { key: 'betaald', label: 'Betaald', color: '#16A34A' },
];

const STATUS_ORDER = STATUS_STAPPEN.map(s => s.key);

// Boekmap: enkel 'kosten' maakt bij goedkeuren een kost aan in het Kostenrapport
const BOEKMAPPEN = [
  { key: 'kosten', label: 'Kosten', hint: 'Komt in het Kostenrapport', bg: '#059669' },
  { key: 'goederen', label: 'Aankoop goederen', hint: 'Handelsgoederen / grondstoffen — geen kost', bg: '#0284C7' },
  { key: 'investering', label: 'Investering', hint: 'Afschrijving — geen kost', bg: '#7C3AED' },
];

export default function InkomendeDocumentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [threeWeg, setThreeWeg] = useState(null);
  const [threeWegLoading, setThreeWegLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mapBusy, setMapBusy] = useState(false);
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState(false);
  const [aiParsing, setAiParsing] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [pdfFit, setPdfFit] = useState('width'); // 'width' = paginabreedte · 'page' = hele pagina zichtbaar
  const [pdfPages, setPdfPages] = useState(0);
  // 🚀 Wachtrij voor "Opslaan & Volgende"-batchverwerking
  const [queue, setQueue] = useState([]);
  const [queueIdx, setQueueIdx] = useState(-1);
  const [catMapping, setCatMapping] = useState({});
  const [customCats, setCustomCats] = useState([]);
  const [regelActief, setRegelActief] = useState(null); // { leverancier, categorie, aantal_correcties }
  const [regelToast, setRegelToast] = useState(null);
  const [leveranciers, setLeveranciers] = useState([]);
  const [showQR, setShowQR] = useState(false);
  const [qrError, setQrError] = useState('');
  const [collapsed, setCollapsed] = useState({ extra: true }); // extra details standaard ingeklapt; rest open (per-lijn: open bij gemengde boeking)
  const [catSearch, setCatSearch] = useState('');
  const [catOpen, setCatOpen] = useState(false);
  const [payModal, setPayModal] = useState(false);
  const catRef = useRef(null);
  const timerRef = useRef(null);
  const savePromiseRef = useRef(null);
  const dirtyRef = useRef(false);
  // formRef houdt altijd de laatste form-state vast — voorkomt stale closure bij setTimeout(autoSave).
  // Cruciaal voor decimaal-typen: setTimeout captureert autoSave van de huidige render, vóór setForm
  // van die render gecommit is. Zonder ref leest autoSave dan de OUDE form en saved we de waarde
  // van keystroke N-1 i.p.v. N.
  const formRef = useRef({});

  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== 'undefined' ? (localStorage.getItem('token') || '') : '';
  const bestandUrl = `${API_BASE}/api/inkomende-documenten/${id}/bestand`;

  const load = async () => {
    setLoading(true);
    try {
      const r = await API.get(`/inkomende-documenten/${id}`);
      setDoc(r.data);
      setForm(r.data);
      formRef.current = r.data;
      // Dubbel-controle (leverancier + factuurnummer bestaat al als kost of inkomende factuur)
      if (r.data.dubbel_van) {
        const m = r.data.dubbel_van;
        setDuplicaatInfo({ type: 'exact', bron: m.bron, match: { id: m.id, leverancier: m.leverancier, factuurnummer: m.factuurnummer, totaal_incl: m.bedrag, datum: m.datum, status: m.status } });
      }
      // 3-weg matching proberen
      try {
        setThreeWegLoading(true);
        const rm = await API.get(`/inkomende-documenten/${id}/3weg-match`);
        setThreeWeg(rm.data);
      } catch (e) {
        // 3-weg-match niet beschikbaar — niet kritiek
      } finally {
        setThreeWegLoading(false);
      }
      // Check of er een regel actief is voor deze leverancier
      const lev = (r.data.leverancier || '').trim();
      if (lev) {
        try {
          const rr = await API.get('/leverancier-regels');
          const match = (rr.data || []).find(x => (x.leverancier_naam || '').toLowerCase() === lev.toLowerCase() && x.actief);
          setRegelActief(match ? {
            leverancier: match.leverancier_naam,
            categorie: match.categorie,
            grootboekrekening: match.grootboekrekening,
            btw_pct: match.btw_pct,
            aftrekbaarheid: match.aftrekbaarheid,
            aantal_correcties: match.correctie_count,
            id: match.id,
          } : null);
        } catch (e) {
          // leverancier-regels niet kritiek
        }
      }
    } catch (e) {
      navigate('/inkomende-documenten');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    setLijnenRaw({}); // reset raw decimal-tekst per lijn bij doc-wissel
    API.get('/categorie-mappings').then(r => {
      const m = {};
      r.data.forEach(x => { m[x.categorie] = { grootboekrekening: x.grootboekrekening, btw_pct: x.btw_pct, aftrekbaarheid: x.aftrekbaarheid }; });
      setCatMapping(m);
    }).catch(() => {});
    API.get('/categorie-custom').then(r => setCustomCats(r.data || [])).catch(() => {});
    API.get('/leveranciers').then(r => setLeveranciers(r.data)).catch(() => {});
    // 🚀 Laad wachtrij van te-verwerken documenten voor "Opslaan & Volgende"-knop
    API.get('/inkomende-documenten').then(r => {
      // Enkel écht nog te verwerken documenten (op status_doc — goedgekeurde/geboekte komen nooit terug in de wachtrij)
      const teDoen = (r.data || []).filter(d => ['te_verwerken', 'verwerkt'].includes(d.status_doc || d.status) || (d.dubbel_van && d.auto_goedgekeurd && !d.dubbel_bevestigd));
      setQueue(teDoen);
      setQueueIdx(teDoen.findIndex(d => d.id === id));
    }).catch(() => {});
  }, [id]);

  // Hou formRef altijd up-to-date met de laatste form-state.
  // Vermijdt stale-closure issue in setTimeout(autoSave) bij snel typen van decimalen.
  useEffect(() => { formRef.current = form; dirtyRef.current = dirty; }, [form, dirty]);
  useEffect(() => () => { if(timerRef.current)clearTimeout(timerRef.current); }, [id]);

  // 🚀 Opslaan & Volgende — bewaart, keurt goed, navigeert naar volgend document in wachtrij
  const opslaanEnVolgende = async () => {
    if (!(await saveAll())) { toast.error('Controleer en bevestig eerst de factuurwijzigingen'); return; }
    try {
      if (['te_verwerken', 'verwerkt'].includes(doc?.status_doc || doc?.status)) {
        const duplicate = duplicaatInfo?.match || doc?.dubbel_van;
        if (duplicate && !window.confirm('Mogelijk dubbele factuur. Heeft u de bestaande factuur nagezien en wilt u deze toch goedkeuren?')) return;
        meldKost(await API.post(`/inkomende-documenten/${id}/goedkeuren`, { boekmap: boekmapKeuze, bevestig_dubbel: !!duplicate }));
      }
    } catch (e) { /* niet kritiek */ }
    const next = queue.find((d, i) => i > queueIdx && d.id !== id);
    if (next) {
      navigate(`/inkomende-documenten/${next.id}`);
    } else {
      alert('🎉 Alle inkomende documenten zijn verwerkt!');
      navigate('/inkomende-documenten');
    }
  };

  // ⏭️ Sla over — alleen navigeren naar volgend document zonder opslaan/goedkeuren.
  // Status blijft 'te_verwerken' zodat het document later opnieuw verschijnt in de wachtrij.
  const slaOver = () => {
    const next = queue.find((d, i) => i > queueIdx && d.id !== id);
    if (next) {
      navigate(`/inkomende-documenten/${next.id}`);
    } else {
      alert('🎉 Geen volgend document. Alle overige documenten zijn verwerkt of overgeslagen.');
      navigate('/inkomende-documenten');
    }
  };

  // ⌨️ Keyboard shortcuts:
  //   Ctrl+Enter        = Opslaan & Volgende
  //   Ctrl+Shift+Enter  = Sla over (zonder opslaan/goedkeuren)
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (e.shiftKey) {
          slaOver();
        } else {
          opslaanEnVolgende();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [opslaanEnVolgende, slaOver]);

  // Close categorie dropdown on outside click
  useEffect(() => {
    if (!catOpen) return;
    const handler = (e) => {
      if (catRef.current && !catRef.current.contains(e.target)) setCatOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [catOpen]);

  const u = (field) => (e) => {
    const v = e?.target ? e.target.value : e;
    const next = { ...formRef.current, [field]: v, ...(field === 'iban' ? { leverancier_iban: v } : {}) };
    formRef.current = next;
    dirtyRef.current = true;
    setForm(next);
    setDirty(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(autoSave, 800);
  };

  const autoSave = async () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (savePromiseRef.current) await savePromiseRef.current;
    const liveForm = formRef.current;
    if (!liveForm?.id || liveForm.id !== id) return false;
    const request = (async () => {
      setSaving(true);
      try {
        const payload = { ...liveForm };
        delete payload._id; delete payload.user_id; delete payload.id;
        const response = await API.put(`/inkomende-documenten/${id}`, payload);
        if (response.data.betaling) {
          setDoc(previous => previous?.id === id ? { ...previous, ...liveForm, ...response.data.betaling } : previous);
        }
        if (formRef.current === liveForm) { dirtyRef.current = false; setDirty(false); }
        return true;
      } catch (e) {
        return false;
      } finally {
        setSaving(false);
      }
    })();
    savePromiseRef.current = request;
    const result = await request;
    if(savePromiseRef.current === request) savePromiseRef.current = null;
    return result;
  };

  const prepareSourceSave = async () => {
    if(timerRef.current){clearTimeout(timerRef.current);timerRef.current=null;}
    if(savePromiseRef.current && !(await savePromiseRef.current))return false;
    return dirtyRef.current ? autoSave() : true;
  };
  const source = useDocumentSource({id, formRef, prepareSave:prepareSourceSave, history:doc?.veld_selecties || [], onDocument: data => {
    if(timerRef.current)clearTimeout(timerRef.current);
    dirtyRef.current=false;
    setDoc(data);setForm(data);formRef.current=data;setDirty(false);
  }});
  const saveAll = () => source.selection ? source.confirm() : autoSave();

  const setCategorie = (cat) => {
    const m = catMapping[cat];
    setForm(p => ({
      ...p, categorie: cat,
      grootboekrekening: m?.grootboekrekening || p.grootboekrekening,
      btw_pct: m?.btw_pct ?? p.btw_pct,
      aftrekbaarheid: m?.aftrekbaarheid ?? p.aftrekbaarheid,
    }));
    setDirty(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(autoSave, 500);
  };

  // ─── Per-regel boekhouding (mixed BTW% / grootboek / categorie per factuurlijn) ───
  // Aparte "raw text" state voor decimaal-velden (BTW%, Aftrek %) per lijn-idx.
  // Voorkomt verlies van komma bij typen: state.lijnenRaw[`${idx}-btw_pct`] = "9,"
  // → input toont exact wat de gebruiker typt, parse gebeurt parallel voor opslaan.
  const [lijnenRaw, setLijnenRaw] = useState({});

  // Update één veld van één lijn in form.lijnen[]
  const updateLijn = (idx, field, value) => {
    setForm(p => {
      const lijnen = [...(p.lijnen || [])];
      if (!lijnen[idx]) return p;
      lijnen[idx] = { ...lijnen[idx], [field]: value };
      return { ...p, lijnen };
    });
    setDirty(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(autoSave, 800);
  };

  // Speciale handler voor decimaal-velden: bewaart raw tekst + parst nummer parallel
  const updateLijnDecimaal = (idx, field, rawValue) => {
    // Bewaar exact wat de gebruiker typte (incl. komma)
    setLijnenRaw(prev => ({ ...prev, [`${idx}-${field}`]: rawValue }));
    // Parse nummer voor opslaan (komma → punt → parseFloat)
    const parsed = parseFloat(String(rawValue).replace(',', '.'));
    updateLijn(idx, field, isNaN(parsed) ? null : parsed);
  };

  // Helper: toon raw-text als die er is, anders geformatteerde DB-waarde
  const lijnDecimaalVal = (idx, field, dbValue) => {
    const rawKey = `${idx}-${field}`;
    if (lijnenRaw[rawKey] !== undefined) return lijnenRaw[rawKey];
    if (dbValue === undefined || dbValue === null) return '';
    return String(dbValue).replace('.', ',');
  };

  // Als gebruiker een categorie per lijn kiest, auto-fill grootboek/btw/aftrekbaar van die lijn
  const setLijnCategorie = (idx, cat) => {
    const m = catMapping[cat];
    setForm(p => {
      const lijnen = [...(p.lijnen || [])];
      if (!lijnen[idx]) return p;
      lijnen[idx] = {
        ...lijnen[idx],
        categorie: cat,
        grootboekrekening: m?.grootboekrekening || lijnen[idx].grootboekrekening || '',
        btw_pct: m?.btw_pct ?? lijnen[idx].btw_pct,
        aftrekbaarheid: m?.aftrekbaarheid ?? lijnen[idx].aftrekbaarheid,
      };
      return { ...p, lijnen };
    });
    setDirty(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(autoSave, 500);
  };

  // Kopieer de globale boekhoud-velden (categorie/grootboek/btw/aftrekbaar) naar ALLE lijnen
  const toepassenOpAlleLijnen = () => {
    setForm(p => {
      const lijnen = (p.lijnen || []).map(l => ({
        ...l,
        categorie: p.categorie || l.categorie || '',
        grootboekrekening: p.grootboekrekening || l.grootboekrekening || '',
        btw_pct: p.btw_pct ?? l.btw_pct,
        aftrekbaarheid: p.aftrekbaarheid ?? l.aftrekbaarheid,
      }));
      return { ...p, lijnen };
    });
    setDirty(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(autoSave, 300);
  };

  // Heuristiek: heeft het document gemengde BTW/grootboek per lijn?
  const lijnenMix = (() => {
    const ll = form.lijnen || [];
    if (ll.length < 2) return false;
    const btws = new Set(ll.map(l => Number(l.btw_pct ?? -1)));
    const groots = new Set(ll.map(l => l.grootboekrekening || ''));
    const cats = new Set(ll.map(l => l.categorie || ''));
    return btws.size > 1 || groots.size > 1 || cats.size > 1;
  })();

  const [duplicaatInfo, setDuplicaatInfo] = useState(null);

  const runAI = async () => {
    setAiParsing(true);
    try {
      const r = await API.post(`/inkomende-documenten/${id}/ai-parse`);
      if (r.data?.data) {
        const ex = r.data.data;
        const regelInfo = ex._regel_toegepast;
        const dup = ex._duplicaat;
        delete ex._regel_toegepast;
        delete ex._duplicaat;
        setForm(p => ({ ...p, ...ex }));
        setDirty(true);
        if (dup) {
          setDuplicaatInfo(dup);
        } else {
          setDuplicaatInfo(null);
        }
        if (regelInfo) {
          setRegelToast(regelInfo);
          setTimeout(() => setRegelToast(null), 10000);
          if (regelInfo.auto_goedkeuren) {
            setTimeout(() => load(), 500);
          }
        }
        setTimeout(autoSave, 300);
      }
    } catch (e) { alert('AI parse mislukt: ' + (e.response?.data?.detail || 'fout')); }
    setAiParsing(false);
  };

  const regelUitschakelen = async () => {
    if (!regelActief?.id) return;
    if (!window.confirm(`Regel voor "${regelActief.leverancier}" uitschakelen? Volgende facturen gebruiken weer de normale AI-herkenning.`)) return;
    try {
      await API.put(`/leverancier-regels/${regelActief.id}`, { actief: false });
      setRegelActief(null);
    } catch (e) {}
  };

  const meldKost = (r) => {
    if (r?.data?.boekmap && r.data.boekmap !== 'kosten') toast.success(`Geklasseerd onder "${r.data.boekmap_label}" — geen kost aangemaakt`);
    else if (r?.data?.aangemaakt) toast.success('Kost automatisch aangemaakt in Kostenrapport (factuurdatum)');
    else if (r?.data?.reden) toast.warning(`Geen kost aangemaakt: ${r.data.reden}`);
    if (r?.data?.kost_bestaand) toast.warning(`Let op: dit document stond al als kost ${r.data.kost_bestaand} in het Kostenrapport — verwijder die daar indien nodig`, { duration: 9000 });
  };
  const boekmapKeuze = form.boekmap || doc?.boekmap || doc?.boekmap_suggestie || 'kosten';
  const setBoekmap = async (map) => {
    if (mapBusy) return;
    if (doc.kost_id && map !== 'kosten' && boekmapKeuze === 'kosten' && !window.confirm('Deze factuur uit het Kostenrapport halen? De factuur, betaling en wijzigingshistoriek blijven bewaard.')) return;
    setMapBusy(true);
    try {
      if(dirty && !(await autoSave())) throw new Error('Bewaar eerst de factuurwijzigingen');
      await API.put(`/inkomende-documenten/${id}`,{boekmap:map});
      const {data}=await API.get(`/inkomende-documenten/${id}`,{noCache:true});
      setDoc(data);setForm(data);formRef.current=data;setDirty(false);
      toast.success(map==='kosten'?'Map gewijzigd naar Kosten':'Map gewijzigd — deze factuur telt niet mee als kost');
    } catch(e) { toast.error(e.response?.data?.detail||e.message||'Map wijzigen mislukt'); }
    finally {setMapBusy(false);}
  };
  // Goedkeuren → kost (enkel map 'Kosten') → document direct klasseren: naar het volgende te verwerken document of terug naar de lijst
  const goedkeuren = async () => {
    const duplicate = duplicaatInfo?.match || doc?.dubbel_van;
    if (duplicate && !window.confirm(`Mogelijk dubbele factuur: ${duplicate.leverancier} ${duplicate.factuurnummer || ''}. Heeft u deze nagezien en wilt u toch goedkeuren?`)) return;
    if (!(await saveAll())) { toast.error('Controleer en bevestig eerst de factuurwijzigingen'); return; }
    try {
      const r = await API.post(`/inkomende-documenten/${id}/goedkeuren`, { boekmap: boekmapKeuze, bevestig_dubbel:!!duplicate });
      meldKost(r);
      toast.success(`Goedgekeurd — ${doc?.factuurnummer || doc?.leverancier || 'document'} staat in de lijst`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Goedkeuren mislukt');
      return;
    }
    const next = queue.find((d, i) => i > queueIdx && d.id !== id);
    navigate(next ? `/inkomende-documenten/${next.id}` : '/inkomende-documenten');
  };
  const afkeuren = async () => {
    const reden = window.prompt('Reden voor afkeuren (optioneel):', '');
    if (reden === null) return; // user cancelled
    try {
      await API.post(`/inkomende-documenten/${id}/afkeuren`, { reden: reden || '' });
      load();
    } catch (e) {
      alert('Fout bij afkeuren: ' + (e.response?.data?.detail || e.message));
    }
  };
  const openPayModal = async () => {
    if(source.selection && !(await source.confirm()))return;
    if (dirty && !(await autoSave())) {
      toast.error('Factuurwijzigingen konden niet bewaard worden. Controleer deze vóór de betaalbevestiging.');
      return;
    }
    try {
      const { data } = await API.get(`/inkomende-documenten/${id}`, { noCache: true });
      setDoc(data); setPayModal(true);
    } catch { toast.error('Het huidige saldo kon niet worden opgehaald'); }
  };
  const naarAccountant = async () => {
    const nieuw = !doc?.naar_accountant;
    await API.post(`/inkomende-documenten/${id}/accountant-status`, { verzonden: nieuw });
    load();
  };

  if (loading || !doc) {
    return <div className="p-8 text-center text-gray-400">Laden...</div>;
  }

  const statusDoc = form.status_doc || doc.status_doc || 'te_verwerken';
  const currentStatusIdx = STATUS_ORDER.indexOf(statusDoc);
  const isPDF = (doc.type || '').toLowerCase() === 'pdf';
  const isImage = ['png', 'jpg', 'jpeg', 'gif', 'tif', 'tiff', 'heic', 'heif', 'webp'].includes((doc.type || '').toLowerCase());
  const isXML = (doc.type || '').toLowerCase() === 'xml';

  // ── Compacte UI-helpers ─────────────────────────────────────────────────────────
  const INP = 'field-input compact w-full';
  const LBL = 'block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-0.5 truncate';

  const bronLabel = doc.is_peppol ? 'Peppol' : (doc.bron === 'email' ? 'E-mail' : (doc.bron || 'Upload'));
  const bronKleur = doc.is_peppol ? '#059669' : (doc.bron === 'email' ? '#0891B2' : '#6B7280');
  const mapping = catMapping[form.categorie] || {};
  const vervalInfo = (() => {
    if (!form.vervaldag) return null;
    try {
      const d = new Date(form.vervaldag); const t = new Date(); t.setHours(0, 0, 0, 0);
      const dagen = Math.round((d - t) / 86400000);
      if (statusDoc === 'betaald') return { txt: 'Betaald', cls: 'bg-emerald-500/30' };
      if (dagen < 0) return { txt: `${Math.abs(dagen)}d te laat`, cls: 'bg-red-500/60' };
      if (dagen === 0) return { txt: 'Vandaag', cls: 'bg-amber-500/60' };
      return { txt: `over ${dagen}d`, cls: 'bg-white/20' };
    } catch { return null; }
  })();
  const extraSub = [form.klantnummer && `Klant ${form.klantnummer}`, form.bestelnummer && `Order ${form.bestelnummer}`, form.incoterm, form.btw_regime && 'Intracom'].filter(Boolean).join(' · ');

  return (
    <IncomingDocumentContext.Provider value={{source,collapsed,setCollapsed}}>
    <div data-testid="inkomende-doc-detail" className="space-y-2 -mt-2" onFocusCapture={(isPDF||isImage)?source.capture:undefined}>
      <style>{`[data-testid="inkomende-doc-detail"] [data-source-active="true"] { outline: 2px solid #002FA7; outline-offset: 1px; background: #EFF4FF; }`}</style>
      {/* Regel-toast (wordt getoond na AI-parse) */}
      {regelToast && (
        <div className={`fixed top-5 right-5 z-50 p-3 max-w-sm shadow-xl border-2 bg-white ${regelToast.auto_goedkeuren ? 'border-emerald-400' : 'border-purple-300'}`} data-testid="regel-toast">
          <div className="flex items-start gap-2 text-xs">
            <div className="flex-1">
              <div className={`font-bold mb-0.5 ${regelToast.auto_goedkeuren ? 'text-emerald-800' : 'text-purple-900'}`}>
                {regelToast.auto_goedkeuren ? 'Auto-goedgekeurd' : 'Persoonlijke regel toegepast'}
              </div>
              <div className="text-slate-700 leading-relaxed">
                {regelToast.auto_goedkeuren
                  ? <>Factuur van <strong>{regelToast.leverancier}</strong> is <strong>direct goedgekeurd</strong> — enkel nog betalen.</>
                  : <>Voor <strong>{regelToast.leverancier}</strong> automatisch ingevuld:</>}
                <span className="block mt-1 text-[11px] text-slate-600">
                  {[regelToast.applied?.categorie && `Categorie ${regelToast.applied.categorie}`, regelToast.applied?.grootboekrekening && `Grootboek ${regelToast.applied.grootboekrekening}`, regelToast.applied?.btw_pct !== undefined && `BTW ${regelToast.applied.btw_pct}%`, regelToast.applied?.aftrekbaarheid !== undefined && `Aftrek ${regelToast.applied.aftrekbaarheid}%`].filter(Boolean).join(' · ')}
                  {' '}· {regelToast.aantal_correcties} correcties geleerd{regelToast.auto_goedkeuren ? ` (drempel ${regelToast.drempel})` : ''}
                </span>
              </div>
            </div>
            <button onClick={() => setRegelToast(null)} className="text-slate-400 hover:text-slate-700"><XCircle size={16} /></button>
          </div>
        </div>
      )}

      {/* ── Sticky actiebalk: terug · nummer · status · map · acties ── */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 -mx-3 px-3 md:-mx-6 md:px-6 py-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid="detail-actiebalk">
        <button className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 shrink-0" onClick={() => navigate('/inkomende-documenten')} data-testid="terug-btn">
          <ArrowLeft size={14} weight="bold" /> Aankopen
        </button>
        <span className="text-sm font-bold font-mono text-slate-900 truncate max-w-[220px]" title={doc.factuurnummer || doc.filename}>{doc.factuurnummer || doc.filename || id}</span>
        <span className="text-[10px] text-slate-400 italic min-w-[70px]" data-testid="document-save-status">{saving || source.busy ? 'Opslaan…' : source.selection ? 'Voorstel nog bevestigen' : dirty ? 'Niet opgeslagen' : 'Opgeslagen'}</span>
        {queueIdx >= 0 && queue.length > 1 && (
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#EFF4FF] border border-[#002FA7]/20" data-testid="queue-progress">
            <span className="text-[10px] font-bold text-[#002FA7]">{queueIdx + 1}/{queue.length}</span>
            <div className="w-14 bg-[#002FA7]/10 h-1 overflow-hidden"><div className="h-full bg-[#002FA7]" style={{ width: `${Math.round(((queueIdx + 1) / queue.length) * 100)}%` }} /></div>
          </div>
        )}

        {/* Status stepper — compact */}
        <div className="inline-flex items-center" data-testid="status-stepper">
          {STATUS_STAPPEN.map((s, i) => {
            const isActive = i === currentStatusIdx; const isDone = i < currentStatusIdx;
            return (
              <div key={s.key} className="inline-flex items-center">
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider" style={{
                  background: isActive ? s.color : (isDone ? `${s.color}18` : '#F1F5F9'),
                  color: isActive ? '#fff' : (isDone ? s.color : '#94A3B8'),
                }} title={s.label}>
                  {isDone && <CheckCircle size={9} weight="fill" />}{s.label}
                </span>
                {i < STATUS_STAPPEN.length - 1 && <CaretRight size={8} className="text-slate-300" />}
              </div>
            );
          })}
        </div>
        {doc.auto_goedgekeurd && <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 text-[9px] font-bold uppercase tracking-wider border border-emerald-200" title="Automatisch goedgekeurd via leverancier-regel" data-testid="auto-approved-badge">⚡ Auto</span>}

        {/* Boekmap */}
        <div className="inline-flex items-center gap-1" data-testid="boekmap-keuze" title="Enkel 'Kosten' komt in het Kostenrapport">
          <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Map</span>
          <div className="inline-flex border border-slate-300 bg-white">
            {BOEKMAPPEN.map(b => {
              const actief = boekmapKeuze === b.key;
              return (
                <button key={b.key} type="button" disabled={mapBusy || saving} onClick={() => setBoekmap(b.key)} title={b.hint}
                  className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50 ${actief ? 'text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                  style={actief ? { background: b.bg } : {}} data-testid={`boekmap-${b.key}`}>
                  {b.label}
                </button>
              );
            })}
          </div>
        </div>
        {doc.kost_id && boekmapKeuze === 'kosten' && (
          <button type="button" onClick={() => navigate(`/kosten-rapport?kost=${doc.kost_id}&datum=${doc.datum || ''}`)}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 text-[9px] font-bold uppercase tracking-wider border border-emerald-200"
            title="Deze factuur staat als kost in het Kostenrapport" data-testid="kost-banner">
            <Wallet size={10} weight="fill" /> Kost
          </button>
        )}

        {/* Acties */}
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <button className="h-7 px-2 inline-flex items-center gap-1 text-[11px] font-semibold bg-white border border-slate-300 text-slate-700 hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors disabled:opacity-50" onClick={runAI} disabled={aiParsing} title="AI herkenning opnieuw uitvoeren" data-testid="ai-parse-btn">
            <MagicWand size={13} weight="bold" /> {aiParsing ? 'AI leest…' : 'AI'}
          </button>
          <a className="h-7 w-7 inline-flex items-center justify-center bg-white border border-slate-300 text-slate-700 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors" href={bestandUrl} target="_blank" rel="noopener noreferrer" title="Download origineel" data-testid="download-btn">
            <DownloadSimple size={13} weight="bold" />
          </a>
          {statusDoc !== 'afgekeurd' && statusDoc !== 'betaald' && (
            <>
              <button className="h-7 px-2.5 inline-flex items-center gap-1 text-[11px] font-bold bg-white border border-red-300 text-red-600 hover:bg-red-600 hover:text-white transition-colors" onClick={afkeuren} data-testid="reject-btn">
                <XCircle size={13} weight="bold" /> Weigeren
              </button>
              <button className="h-7 px-3 inline-flex items-center gap-1 text-[11px] font-bold bg-[#059669] text-white hover:bg-[#047857] transition-colors" onClick={goedkeuren} data-testid="approve-btn">
                <CheckCircle size={13} weight="bold" /> Goedkeuren
              </button>
            </>
          )}
          {queue.length > 1 && queueIdx >= 0 && queueIdx < queue.length - 1 && (
            <>
              <button className="h-7 px-2.5 inline-flex items-center gap-1 text-[11px] font-bold bg-[#002FA7] text-white hover:bg-[#002380] transition-colors" onClick={opslaanEnVolgende} title="Bewaren + goedkeuren + volgende (Ctrl+Enter)" data-testid="save-and-next-btn">
                Opslaan & Volgende <ArrowRight size={13} weight="bold" />
              </button>
              <button className="h-7 px-2 inline-flex items-center gap-1 text-[11px] font-bold bg-white border border-amber-300 text-amber-700 hover:bg-amber-50 transition-colors" onClick={slaOver} title="Sla over — naar volgend document (Ctrl+Shift+Enter)" data-testid="skip-next-btn">
                <SkipForward size={13} weight="bold" />
              </button>
            </>
          )}
          {statusDoc !== 'afgekeurd' && doc.doc_type !== 'creditnota' && doc.openstaand > 0 && (
            <button className="h-7 px-2.5 inline-flex items-center gap-1 text-[11px] font-bold bg-[#002FA7] text-white hover:bg-[#002380] transition-colors" onClick={openPayModal} disabled={saving} data-testid="paid-btn">
              <CurrencyEur size={13} weight="bold" /> Betaling bevestigen
            </button>
          )}
        </div>
      </div>

      {/* Duplicaat — één regel */}
      {duplicaatInfo && duplicaatInfo.match && (
        <div className="bg-red-50 border border-red-300 border-l-4 border-l-red-600 px-3 py-1.5 flex items-center gap-2 flex-wrap text-xs text-red-800" data-testid="duplicaat-waarschuwing">
          <Warning size={14} weight="fill" className="text-[#D97706] shrink-0" />
          <span className="font-bold text-red-800">Dubbel — nazien vóór goedkeuren</span>
          <span className="text-amber-800 truncate">
            {duplicaatInfo.bron === 'kost' ? 'bestaat al als kost' : 'bestaat al als inkomende factuur'}: <strong>{duplicaatInfo.match.leverancier}</strong> · {duplicaatInfo.match.factuurnummer || '—'} · € {duplicaatInfo.match.totaal_incl || 0} · {duplicaatInfo.match.datum || '—'} · {duplicaatInfo.match.status || '—'}
          </span>
          <a href={duplicaatInfo.bron === 'kost' ? `/kosten-rapport?kost=${duplicaatInfo.match.id}&datum=${duplicaatInfo.match.datum || ''}` : `/inkomende-documenten/${duplicaatInfo.match.id}`}
            className="ml-auto px-2 py-0.5 text-[11px] font-bold bg-[#D97706] text-white hover:bg-[#B45309]" data-testid="duplicaat-open">
            {duplicaatInfo.bron === 'kost' ? 'Open kost →' : 'Open factuur →'}
          </a>
          <button onClick={() => setDuplicaatInfo(null)} className="px-2 py-0.5 text-[11px] font-bold border border-amber-400 text-amber-800 hover:bg-amber-100" data-testid="duplicaat-negeer">Negeren</button>
        </div>
      )}

      {/* Regel actief — één regel */}
      {regelActief && (
        <div className="bg-[#EFF4FF] border border-[#002FA7]/20 border-l-4 border-l-[#002FA7] px-3 py-1.5 flex items-center gap-2 flex-wrap text-xs" data-testid="regel-actief-banner">
          <span className="font-bold text-[#002FA7]">Regel actief · {regelActief.leverancier}</span>
          <span className="text-slate-700">
            {[regelActief.categorie, regelActief.grootboekrekening && `GB ${regelActief.grootboekrekening}`, (regelActief.btw_pct !== undefined && regelActief.btw_pct !== null) && `BTW ${regelActief.btw_pct}%`, (regelActief.aftrekbaarheid !== undefined && regelActief.aftrekbaarheid !== null) && `Aftrek ${regelActief.aftrekbaarheid}%`].filter(Boolean).join(' · ')}
          </span>
          <span className="text-slate-400 text-[11px]">{regelActief.aantal_correcties} correcties</span>
          <button onClick={regelUitschakelen} className="ml-auto text-[11px] text-[#002FA7] hover:underline font-semibold" data-testid="regel-uitschakelen">Uitschakelen</button>
        </div>
      )}

      {/* 3-weg matching */}
      {(threeWeg && threeWeg.po_kandidaten && threeWeg.po_kandidaten.length > 0) && (
        <div className="bg-white border border-slate-200" data-testid="3weg-matching">
          <div className="px-3 py-1.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
            <CheckSquare size={13} className="text-[#002FA7]" weight="bold" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">3-weg matching</span>
            {threeWeg.best_match && threeWeg.best_match.match_score >= 100 && <span className="text-[9px] font-bold px-1.5 py-0.5 bg-emerald-600 text-white">PERFECTE MATCH</span>}
            {threeWeg.best_match && threeWeg.best_match.match_score >= 80 && threeWeg.best_match.match_score < 100 && <span className="text-[9px] font-bold px-1.5 py-0.5 bg-amber-500 text-white">WAARSCHIJNLIJK</span>}
            <span className="ml-auto text-[10px] text-slate-500">{threeWeg.po_kandidaten.length} kandida{threeWeg.po_kandidaten.length === 1 ? 'at' : 'ten'}</span>
          </div>
          <div className="divide-y divide-slate-100">
            {threeWeg.po_kandidaten.slice(0, 5).map(po => {
              const matchColor = po.match_score >= 100 ? '#059669' : po.match_score >= 80 ? '#D97706' : '#94A3B8';
              return (
                <div key={po.id} className="px-3 py-1.5 flex items-center gap-3 text-xs hover:bg-slate-50" data-testid={`po-match-${po.id}`}>
                  <span className="font-mono font-bold text-[#002FA7] min-w-[90px]">{po.id}</span>
                  <span className="text-slate-500 min-w-[72px]">{po.datum}</span>
                  <span className="text-[9px] font-bold px-1.5 py-0.5 bg-slate-100 text-slate-700 uppercase">{po.status}</span>
                  <span className="text-slate-500">{po.aantal_lijnen} lijn{po.aantal_lijnen === 1 ? '' : 'en'}</span>
                  <span className="font-bold text-slate-900">{fmt(po.totaal)}</span>
                  <span className={`font-bold ${po.diff === 0 ? 'text-emerald-700' : po.diff > 0 ? 'text-red-600' : 'text-purple-600'}`}>{po.diff === 0 ? '= match' : po.diff > 0 ? `+${fmt(po.diff)}` : fmt(po.diff)}</span>
                  <div className="ml-auto flex items-center gap-2">
                    <div className="w-12 h-1 bg-slate-200 overflow-hidden"><div className="h-full" style={{ width: `${po.match_score}%`, background: matchColor }} /></div>
                    <span className="text-[10px] font-bold" style={{ color: matchColor }}>{po.match_score}%</span>
                    {po.reeds_gekoppeld ? <span className="text-[10px] text-slate-400 italic">gekoppeld</span> : (
                      <button className="text-[10px] font-bold px-2 py-0.5 bg-[#002FA7] text-white hover:bg-[#002380] inline-flex items-center gap-1"
                        onClick={async () => {
                          if (!window.confirm(`Inkomende factuur ${doc.factuurnummer || id} koppelen aan PO ${po.id}?`)) return;
                          try { await API.post(`/inkomende-documenten/${id}/koppel-po`, { po_id: po.id }); load(); } catch (e) { alert(e.response?.data?.detail || 'Koppeling mislukt'); }
                        }} data-testid={`koppel-${po.id}`}>
                        <LinkSimple size={10} weight="bold" /> Koppel
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {doc.po_id && (
            <div className="px-3 py-1 bg-emerald-50 border-t border-emerald-200 flex items-center gap-2 text-[11px]">
              <CheckCircle size={12} weight="fill" className="text-emerald-600" /> <span className="font-semibold text-emerald-900">Gekoppeld aan</span>
              <span className="font-mono font-bold text-[#002FA7]">{doc.po_id}</span>
              {doc.po_matched_at && <span className="text-[10px] text-slate-500">({new Date(doc.po_matched_at).toLocaleDateString('nl-BE')})</span>}
            </div>
          )}
        </div>
      )}

      {/* ── Split: formulier links (7) · document rechts (5) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-start">
        <div className="lg:col-span-7 space-y-2">
          {/* KPI-strip */}
          <div className="bg-gradient-to-r from-[#002FA7] to-[#1E40AF] text-white px-3 py-2 grid grid-cols-2 md:grid-cols-5 gap-x-4 gap-y-2" data-testid="cockpit-banner">
            <div className="min-w-0 md:col-span-2">
              <div className="text-[9px] uppercase tracking-wider text-white/70 font-bold">Leverancier</div>
              <div className="text-sm font-bold truncate" title={form.leverancier}>{form.leverancier || '—'}</div>
              <div className="text-[10px] text-white/70 font-mono truncate">{form.leverancier_btw || form.iban || form.leverancier_iban || ''}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-wider text-white/70 font-bold">Factuur</div>
              <div className="text-sm font-bold font-mono truncate">{form.factuurnummer || '—'}</div>
              <div className="text-[10px] text-white/70">{form.datum || ''}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-wider text-white/70 font-bold">Totaal incl.</div>
              <div className="text-lg font-black font-mono tracking-tight leading-tight" data-testid="cockpit-totaal">{fmt(form.totaal || form.totaal_incl || 0)}</div>
              {form.excl ? <div className="text-[10px] text-white/70 font-mono">{fmt(form.excl)} excl.</div> : null}
            </div>
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-wider text-white/70 font-bold">Vervaldag</div>
              <div className="text-sm font-bold font-mono">{form.vervaldag || '—'}</div>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {vervalInfo && <span className={`text-[9px] px-1 py-px font-bold ${vervalInfo.cls}`}>{vervalInfo.txt}</span>}
                {form.btw_regime && form.btw_regime.toLowerCase().includes('intracom') && <span className="text-[9px] bg-white/20 px-1 py-px font-bold">INTRACOM</span>}
                {form.incoterm && <span className="text-[9px] bg-white/20 px-1 py-px font-bold uppercase">{form.incoterm}</span>}
                {form.po_referentie && <span className="text-[9px] bg-white/20 px-1 py-px font-mono">PO {form.po_referentie}</span>}
              </div>
            </div>
          </div>

          {/* Factuur & leverancier — één dichte kaart */}
          <Kaart icon={Buildings} title="Factuur" sub={`${form.doc_type || 'factuur'} · ${form.leverancier || 'leverancier?'}`} kleur="#002FA7" kKey="doc" testId="kaart-factuur">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Veld label="Leverancier" span="col-span-2"><input className={INP} value={form.leverancier || ''} onChange={u('leverancier')} data-testid="f-leverancier" /></Veld>
              <Veld label="BTW-nummer"><input className={`${INP} font-mono`} value={form.leverancier_btw || ''} onChange={u('leverancier_btw')} placeholder="BE 0123.456.789" data-testid="f-leverancier-btw" /></Veld>
              <Veld label="IBAN"><input className={`${INP} font-mono`} value={form.iban ?? form.leverancier_iban ?? ''} onChange={u('iban')} placeholder="BE00 0000 0000 0000" data-testid="f-leverancier-iban" /></Veld>
              <Veld label="E-mail"><input className={INP} value={form.leverancier_email || ''} onChange={u('leverancier_email')} data-testid="f-leverancier-email" /></Veld>
              <Veld label="Telefoon"><input className={INP} value={form.leverancier_tel || ''} onChange={u('leverancier_tel')} data-testid="f-leverancier-tel" /></Veld>
              <Veld label="Type">
                <select className={INP} value={form.doc_type || 'factuur'} onChange={u('doc_type')} data-testid="f-doc-type">
                  <option value="factuur">Factuur</option><option value="creditnota">Creditnota</option><option value="kasticket">Kasticket</option>
                </select>
              </Veld>
              <Veld label="Factuurnummer"><input className={`${INP} font-mono`} value={form.factuurnummer || ''} onChange={u('factuurnummer')} data-testid="f-factuurnummer" /></Veld>
              <Veld label="Factuurdatum"><input type="date" className={INP} value={form.datum || ''} onChange={u('datum')} data-testid="f-datum" /></Veld>
              <Veld label="Vervaldatum"><input type="date" className={INP} value={form.vervaldag || ''} onChange={u('vervaldag')} data-testid="f-vervaldag" /></Veld>
              <Veld label="Excl. BTW"><input type="number" step="0.01" className={`${INP} font-mono text-right`} value={form.excl || ''} onChange={u('excl')} data-testid="f-excl" /></Veld>
              <Veld label="Incl. BTW"><input type="number" step="0.01" className={`${INP} font-mono text-right font-bold`} value={form.totaal || ''} onChange={u('totaal')} data-testid="f-totaal" /></Veld>
            </div>
          </Kaart>

          {/* Boekhouding */}
          <Kaart icon={Receipt} title="Boekhouding" sub={`${form.categorie || 'categorie?'} · GB ${form.grootboekrekening || '—'} · BTW ${form.btw_pct ?? '—'}%`} kleur="#059669" kKey="boek" testId="kaart-boekhouding"
            badge={mapping.grootboekrekening ? <span className="text-[9px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1 py-px font-bold">AUTO</span> : null}>
            <div className="grid grid-cols-3 md:grid-cols-[2fr_1fr_0.7fr_0.7fr] gap-2">
              <div ref={catRef} className="relative col-span-3 md:col-span-1">
                <label className={LBL}>Categorie</label>
                <div className="relative">
                  <input className={`${INP} pr-7 cursor-pointer`} value={catOpen ? catSearch : (form.categorie || '')}
                    onChange={e => { setCatSearch(e.target.value); setCatOpen(true); }} onFocus={() => { setCatSearch(''); setCatOpen(true); }}
                    placeholder="Zoek of kies…" data-testid="f-categorie" />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">{catOpen ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />}</div>
                </div>
                {catOpen && (() => {
                  const q = catSearch.toLowerCase().trim();
                  const allGroups = CATEGORIE_GROEPEN.map(g => ({ ...g, items: [...g.items] }));
                  if (customCats.length > 0) {
                    const groepMap = {};
                    customCats.forEach(c => { const g = c.groep || 'Eigen categorieën'; (groepMap[g] = groepMap[g] || []).push(c.naam); });
                    Object.entries(groepMap).forEach(([g, items]) => {
                      const existing = allGroups.find(x => x.groep === g);
                      if (existing) existing.items = [...existing.items, ...items]; else allGroups.push({ groep: g, items, _custom: true });
                    });
                  }
                  const filtered = allGroups.map(g => {
                    if (!q) return g;
                    const matchGroup = g.groep.toLowerCase().includes(q);
                    const matchItems = g.items.filter(it => it.toLowerCase().includes(q));
                    return (matchGroup || matchItems.length > 0) ? { ...g, items: matchGroup ? g.items : matchItems } : null;
                  }).filter(Boolean);
                  const total = filtered.reduce((s, g) => s + g.items.length + 1, 0);
                  return (
                    <div className="absolute z-20 mt-1 w-full min-w-[280px] bg-white border border-slate-300 shadow-lg max-h-72 overflow-auto" data-testid="cat-dropdown">
                      {total === 0 ? (
                        <div className="p-3 text-center text-xs text-slate-400">
                          Geen resultaten voor <strong>"{q}"</strong>
                          <div className="mt-1"><button className="text-[#002FA7] hover:underline font-semibold" onClick={() => navigate('/instellingen')}>+ Nieuwe categorie</button></div>
                        </div>
                      ) : filtered.map((g, gi) => (
                        <div key={g.groep + gi}>
                          <div className="px-2 py-1 bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-y border-slate-100">{g._custom && <span className="text-[#D97706]">★ </span>}{g.groep}</div>
                          <button onClick={() => { setCategorie(g.groep); setCatOpen(false); setCatSearch(''); }} className="w-full px-2 py-1 text-left text-xs hover:bg-[#EFF4FF] text-slate-500 italic">{g.groep} <span className="text-slate-400">(hoofd)</span></button>
                          {g.items.map(it => (
                            <button key={it} onClick={() => { setCategorie(it); setCatOpen(false); setCatSearch(''); }} className="w-full px-2 py-1 pl-5 text-left text-xs hover:bg-[#EFF4FF] text-slate-800">↳ {it}</button>
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <Veld label="Grootboek"><input className={`${INP} font-mono`} value={form.grootboekrekening || ''} onChange={u('grootboekrekening')} placeholder="6…" data-testid="f-grootboek" /></Veld>
              <Veld label="BTW %"><input type="number" className={`${INP} font-mono text-right`} value={form.btw_pct ?? ''} onChange={u('btw_pct')} data-testid="f-btw-pct" /></Veld>
              <Veld label="Aftrekbaar %"><input type="number" className={`${INP} font-mono text-right`} value={form.aftrekbaarheid ?? ''} onChange={u('aftrekbaarheid')} data-testid="f-aftrekbaarheid" /></Veld>
            </div>
            <div className="mt-1.5 text-[10px] text-slate-500">Categorie kiezen vult grootboek, BTW % en aftrekbaarheid automatisch in (Belgisch rekeningstelsel).</div>
          </Kaart>

          {/* Boekhoudregels per lijn — standaard ingeklapt tenzij gemengd */}
          {(form.lijnen || []).length > 0 && (
            <Kaart icon={Receipt} title="Per lijn" kKey="lijnenboek" defaultOpen={!!lijnenMix} testId="boekhoudregels-section" kleur={lijnenMix ? '#D97706' : '#64748B'}
              sub={lijnenMix ? `gemengde BTW / grootboek — ${form.lijnen.length} lijnen` : `${form.lijnen.length} lijn${form.lijnen.length > 1 ? 'en' : ''} · zelfde boeking`}
              badge={lijnenMix ? <span className="text-[9px] bg-amber-50 text-amber-700 border border-amber-200 px-1 py-px font-bold">GEMENGD</span> : null}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] text-slate-500">Leeg = globale boeking · <span className="text-amber-700 font-semibold">amber</span> = eigen waarde</span>
                <button type="button" onClick={toepassenOpAlleLijnen} disabled={!form.categorie && !form.grootboekrekening && form.btw_pct === undefined}
                  className="h-6 px-2 text-[10px] font-bold bg-white border border-slate-300 text-[#002FA7] hover:bg-[#EFF4FF] disabled:opacity-40" data-testid="toepassen-op-alle-lijnen">
                  ⬇ Globaal naar alle lijnen
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="lijnen-tabel">
                  <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500">
                    <tr><th className="px-1.5 py-1 text-left w-5">#</th><th className="px-1.5 py-1 text-left">Omschrijving</th><th className="px-1.5 py-1 text-right w-20">Bedrag</th><th className="px-1.5 py-1 text-left w-36">Categorie</th><th className="px-1.5 py-1 text-left w-20">GB</th><th className="px-1.5 py-1 text-right w-14">BTW%</th><th className="px-1.5 py-1 text-right w-16">Aftrek%</th></tr>
                  </thead>
                  <tbody>
                    {form.lijnen.map((l, idx) => {
                      const heeftEigen = !!(l.categorie || l.grootboekrekening || l.btw_pct !== undefined);
                      const cell = 'w-full text-[11px] border border-slate-200 hover:border-[#002FA7] focus:border-[#002FA7] px-1 py-0.5 bg-white outline-none';
                      return (
                        <tr key={idx} className={`border-t border-slate-100 ${heeftEigen ? 'bg-amber-50/40' : ''}`} data-testid={`lijn-row-${idx}`}>
                          <td className="px-1.5 py-1 text-slate-400 font-mono">{idx + 1}</td>
                          <td className="px-1.5 py-1 truncate max-w-[260px]" title={l.omschr || ''}>{l.omschr || '—'}{l.art_code && <span className="ml-1 text-[9px] text-slate-400 font-mono">{l.art_code}</span>}</td>
                          <td className="px-1.5 py-1 text-right font-mono">{Number(l.bedrag || 0).toFixed(2)}</td>
                          <td className="px-1.5 py-1">
                            <select className={cell} value={l.categorie || ''} onChange={e => setLijnCategorie(idx, e.target.value)} data-testid={`lijn-categorie-${idx}`}>
                              <option value="">— globaal —</option>
                              {CATEGORIE_GROEPEN.map(g => <optgroup key={g.groep} label={g.groep}>{g.items.map(it => <option key={it} value={it}>{it}</option>)}</optgroup>)}
                            </select>
                          </td>
                          <td className="px-1.5 py-1"><input type="text" className={`${cell} font-mono`} value={l.grootboekrekening || ''} onChange={e => updateLijn(idx, 'grootboekrekening', e.target.value)} placeholder="—" data-testid={`lijn-grootboek-${idx}`} /></td>
                          <td className="px-1.5 py-1"><input type="text" inputMode="decimal" className={`${cell} font-mono text-right`} value={lijnDecimaalVal(idx, 'btw_pct', l.btw_pct)} onChange={e => updateLijnDecimaal(idx, 'btw_pct', e.target.value)} placeholder="—" data-testid={`lijn-btw-${idx}`} /></td>
                          <td className="px-1.5 py-1"><input type="text" inputMode="decimal" className={`${cell} font-mono text-right`} value={lijnDecimaalVal(idx, 'aftrekbaarheid', l.aftrekbaarheid)} onChange={e => updateLijnDecimaal(idx, 'aftrekbaarheid', e.target.value)} placeholder="—" data-testid={`lijn-aftrek-${idx}`} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t border-slate-300">
                    <tr>
                      <td colSpan="2" className="px-1.5 py-1 text-right font-bold text-slate-700">Totaal lijnen</td>
                      <td className="px-1.5 py-1 text-right font-mono font-bold">{form.lijnen.reduce((s, l) => s + Number(l.bedrag || 0), 0).toFixed(2)}</td>
                      <td colSpan="4" className="px-1.5 py-1 text-[10px] text-slate-500">doc. excl. <strong>{Number(form.totaal_excl || form.excl || 0).toFixed(2)}</strong>{lijnenMix && <span className="ml-2 text-amber-700">controleer per lijn vóór accountant</span>}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Kaart>
          )}

          {/* Extra factuurdetails — standaard ingeklapt */}
          <Kaart icon={FileText} title="Extra details" sub={extraSub || 'klantnr · PO · bestelnr · incoterm · btw-regime'} kleur="#0284C7" kKey="extra" defaultOpen={false} testId="kaart-extra">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Veld label="Klantnr (bij lev.)"><input className={`${INP} font-mono`} value={form.klantnummer || ''} onChange={u('klantnummer')} data-testid="f-klantnummer" /></Veld>
              <Veld label="Uw PO-referentie"><input className={`${INP} font-mono`} value={form.po_referentie || ''} onChange={u('po_referentie')} data-testid="f-po-referentie" /></Veld>
              <Veld label="Bestelnr (lev.)"><input className={`${INP} font-mono`} value={form.bestelnummer || ''} onChange={u('bestelnummer')} data-testid="f-bestelnummer" /></Veld>
              <Veld label="Leveringsnr"><input className={`${INP} font-mono`} value={form.leveringsnummer || ''} onChange={u('leveringsnummer')} data-testid="f-leveringsnummer" /></Veld>
              <Veld label="Incoterm"><input className={INP} value={form.incoterm || ''} onChange={u('incoterm')} placeholder="DAP, EXW, FCA…" data-testid="f-incoterm" /></Veld>
              <Veld label="Land bestemming"><input className={`${INP} font-mono uppercase`} value={form.land_bestemming || ''} onChange={u('land_bestemming')} maxLength={3} placeholder="BE" data-testid="f-land-bestemming" /></Veld>
              <Veld label="Betaalwijze"><input className={INP} value={form.betaalwijze || ''} onChange={u('betaalwijze')} placeholder="30 dagen…" data-testid="f-betaalwijze" /></Veld>
              <Veld label={<>BTW-regime {form.btw_regime && form.btw_regime.toLowerCase().includes('intracom') && <span className="ml-1 text-[9px] bg-[#EFF4FF] text-[#002FA7] px-1 font-bold">INTRACOM</span>}</>}>
                <input className={INP} value={form.btw_regime || ''} onChange={u('btw_regime')} placeholder="bv. art. 138" data-testid="f-btw-regime" />
              </Veld>
            </div>
          </Kaart>

          {/* Betaling */}
          <Kaart icon={CreditCard} title="Betaling" kleur={statusDoc === 'betaald' ? '#16A34A' : '#D97706'} kKey="bet" testId="kaart-betaling"
            sub={statusDoc === 'betaald' ? `betaald op ${doc.betaald_datum || '-'}` : (form.mededeling || form.ogm || 'mededeling?')}>
            <InkomendeBetalingen doc={doc} onConfirm={openPayModal} onSaved={load} />
            <div className="grid grid-cols-2 md:grid-cols-[1fr_1fr_2fr_auto] gap-2 items-end">
              <Veld label="Nog te betalen"><input className={`${INP} font-mono text-right font-bold`} value={fmt(doc.openstaand ?? doc.totaal ?? 0)} readOnly data-testid="f-te-betalen" /></Veld>
              <Veld label="Vervaldatum"><input type="date" className={INP} value={form.vervaldag || ''} onChange={u('vervaldag')} data-testid="f-vervaldag-bet" /></Veld>
              <Veld label="Mededeling (OGM)" span="col-span-2 md:col-span-1"><input className={`${INP} font-mono`} value={form.mededeling ?? form.ogm ?? ''} onChange={u('mededeling')} data-testid="f-mededeling" /></Veld>
              <button type="button" onClick={() => { setShowQR(!showQR); setQrError(''); }}
                disabled={!doc.openstaand}
                className="h-[30px] px-2.5 inline-flex items-center gap-1 text-[11px] font-bold bg-white border border-[#D97706] text-[#D97706] hover:bg-[#D97706] hover:text-white transition-colors col-span-2 md:col-span-1" data-testid="show-qr-btn">
                <CreditCard size={13} weight="bold" /> {showQR ? 'Verberg QR' : 'Bank-QR'}
              </button>
            </div>
            {showQR && (
              <div className="mt-2 p-2 bg-[#FFFBEB] border border-[#FCD34D] flex items-start gap-3" data-testid="qr-panel">
                <div className="bg-white p-1.5"><img src={`${API_BASE}/api/inkomende-documenten/${id}/sepa-qr?t=${Date.now()}`} alt="SEPA QR" style={{ width: 120, height: 120 }} onError={() => setQrError('Kan QR niet genereren — vul IBAN en te betalen bedrag in.')} /></div>
                <div className="flex-1 text-[11px] text-slate-700 leading-relaxed">
                  <div className="font-bold text-slate-900 text-xs mb-0.5">Scan met uw bank-app</div>
                  {qrError ? <div className="text-red-700 font-medium">{qrError}</div> : <>KBC, Belfius, BNP, ING, Argenta, Beobank — alle gegevens staan al ingevuld, enkel bevestigen. <span className="text-slate-400">EPC069-12</span></>}
                </div>
              </div>
            )}
          </Kaart>

          {/* Herkomst — één regel */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-[11px] text-slate-500" data-testid="herkomst-regel">
            <span className="px-1.5 py-px font-bold uppercase tracking-wider text-[9px]" style={{ background: `${bronKleur}20`, color: bronKleur }}>{bronLabel}</span>
            <span>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</span>
            <span className="truncate max-w-[260px]" title={doc.filename}>{doc.filename} · {doc.pages || 1} p. · {((doc.size || 0) / 1024).toFixed(0)} KB</span>
            <button onClick={naarAccountant} className="ml-auto inline-flex items-center gap-1 px-1.5 py-px text-[9px] font-bold uppercase tracking-wider border transition-colors" style={{
              background: doc.naar_accountant ? '#059669' : '#fff', color: doc.naar_accountant ? '#fff' : '#64748B', borderColor: doc.naar_accountant ? '#059669' : '#E2E8F0',
            }} data-testid="accountant-toggle">
              {doc.naar_accountant ? <><CheckSquare size={10} weight="fill" /> Naar accountant</> : 'Accountant: nog niet'}
            </button>
          </div>
        </div>

        {/* RECHTS — document, sticky */}
        <div className="lg:col-span-5 lg:sticky lg:top-[58px] min-w-0 bg-slate-100 border border-slate-200 flex flex-col" style={{ height: 'calc(100vh - 170px)', minHeight: 480 }} data-testid="pdf-pane">
          <div className="px-3 py-1.5 bg-[#002FA7] text-white flex items-center gap-2 flex-wrap shrink-0">
            <Paperclip size={12} className="shrink-0" /><span className="text-[11px] font-medium truncate flex-1">{doc.filename}{pdfPages ? <span className="text-white/70"> · {pdfPages} p.</span> : null}</span>
            {isPDF && (
              <div className="inline-flex border border-white/30 mr-1" data-testid="pdf-fit-toggle">
                <button onClick={() => { setPdfFit('page'); setZoom(100); }} className={`px-1.5 py-0.5 text-[10px] font-bold ${pdfFit === 'page' ? 'bg-white text-[#002FA7]' : 'hover:bg-white/10'}`} title="Hele pagina zichtbaar" data-testid="pdf-fit-page">Pagina</button>
                <button onClick={() => { setPdfFit('width'); setZoom(100); }} className={`px-1.5 py-0.5 text-[10px] font-bold ${pdfFit === 'width' ? 'bg-white text-[#002FA7]' : 'hover:bg-white/10'}`} title="Paginabreedte" data-testid="pdf-fit-width">Breedte</button>
              </div>
            )}
            <button onClick={() => setZoom(Math.max(50, zoom - 25))} className="w-6 h-6 hover:bg-white/10 text-xs font-bold" data-testid="pdf-zoom-out">−</button>
            <span className="text-[10px] font-mono w-9 text-center">{zoom}%</span>
            <button onClick={() => setZoom(Math.min(300, zoom + 25))} className="w-6 h-6 hover:bg-white/10 text-xs font-bold" data-testid="pdf-zoom-in">+</button>
            <a href={bestandUrl} target="_blank" rel="noopener noreferrer" className="ml-1 text-[10px] hover:underline" data-testid="pdf-open">Openen</a>
          </div>
          {(isPDF||isImage) && <DocumentSourcePanel source={source} doc={doc} />}
          <div className={`flex-1 min-h-0 min-w-0 ${isPDF||isImage ? '' : 'overflow-auto bg-slate-200 p-2 flex items-start justify-center'}`}>
            {isPDF && <PdfViewer url={bestandUrl} zoom={zoom} fit={pdfFit} onPages={setPdfPages} docId={id} activeField={source.field} onSelect={source.onSelect} selected={source.selection || [...(doc.veld_selecties||[])].reverse().find(s=>!s.ongedaan&&s.veld===source.field)} />}
            {isImage && <DocumentImageViewer docId={id} zoom={zoom} active={source.field} onSelect={source.onSelect} selected={source.selection || [...(doc.veld_selecties||[])].reverse().find(s=>!s.ongedaan&&s.veld===source.field)} />}
            {isXML && (
              <div className="bg-white p-5 w-full max-w-lg text-xs font-mono text-slate-700 whitespace-pre-wrap" style={{ width: `${zoom}%` }}>
                <div className="flex items-center gap-2 mb-2 text-emerald-700 font-bold"><CheckCircle size={14} weight="fill" /> UBL/Peppol XML — automatisch verwerkt</div>
                Gegevens zijn elektronisch overgenomen uit de XML. Geen OCR nodig.
                <a href={bestandUrl} target="_blank" rel="noopener noreferrer" className="block mt-3 text-[#002FA7] font-sans">Open ruwe XML →</a>
              </div>
            )}
            {!isPDF && !isImage && !isXML && (
              <div className="bg-white p-6 text-center text-slate-500 text-sm">
                <FileText size={28} weight="light" className="mx-auto mb-2 text-slate-300" /> Geen voorbeeld voor dit bestandstype.
                <a href={bestandUrl} target="_blank" rel="noopener noreferrer" className="block mt-2 text-[#002FA7] font-medium">Bestand openen →</a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Use token to avoid unused var warning */}
      <input type="hidden" value={token} />

      {payModal && <InkomendeBetalingDialog doc={doc} onClose={() => setPayModal(false)} onSaved={load} />}
    </div>
    </IncomingDocumentContext.Provider>
  );
}
